/**
 * WorkflowDiagram — visual horizontal stepper for workflow stages.
 * Reused for:
 *   • Template preview / editor (props: stages)
 *   • Instance detail        (props: stages, currentStageIndex, stageState, status)
 */
import React from 'react';
import { CheckCircle2, Users, Clock, ArrowRight, AlertTriangle, XCircle, RotateCcw } from 'lucide-react';

/**
 * @param {{
 *   stages: Array<{name, mode, reviewers, allowed_actions, escalation}>,
 *   currentStageIndex?: number,
 *   stageState?: Object,        // {[i]: {approvals, due_at, escalated, overdue_notified}}
 *   status?: string,            // IN_PROGRESS | COMPLETED | REJECTED | REVISE_REQUIRED | CANCELLED
 *   compact?: boolean,          // Tighter version for row previews
 *   onStageClick?: (index)=>void
 * }} props
 */
export default function WorkflowDiagram({ stages = [], currentStageIndex = -1, focusStageIndex = -1, stageState = {}, status, compact = false, onStageClick }) {
  if (!stages.length) return <div className="text-xs text-slate-500 italic">No stages defined yet.</div>;

  const now = new Date();

  return (
    <div className={`flex items-stretch gap-1 ${compact ? 'flex-nowrap overflow-x-auto pb-1 pt-2 pl-2' : 'flex-wrap pt-2 pl-2'}`} data-testid="workflow-diagram">
      {stages.map((s, i) => {
        const reviewers = s.reviewers || [];
        const ss = stageState[String(i)] || { approvals: [] };
        const dueAt = ss.due_at ? new Date(ss.due_at) : null;
        const overdue = dueAt && dueAt < now;
        const escalated = ss.escalated;
        const isDone   = status === 'COMPLETED' || (currentStageIndex > i && status !== 'REJECTED');
        const isCurrent = currentStageIndex === i && (status === 'IN_PROGRESS' || status === 'REVISE_REQUIRED');
        const isRejectedHere = status === 'REJECTED' && currentStageIndex === i;
        const isFocused = focusStageIndex === i && !isCurrent;   // Only show focus ring if it's NOT already the current stage

        // Color scheme
        const cls = isRejectedHere ? 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300'
                  : isCurrent      ? (overdue ? 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300 ring-2 ring-red-500'
                                             : 'border-sky-500 bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-2 ring-sky-500')
                  : isDone         ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  :                  'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300';

        const focusRing = isFocused ? 'ring-2 ring-fuchsia-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900' : '';

        const reviewerLabels = reviewers
          .slice(0, compact ? 1 : 3)
          .map(r => r.email || r.role || 'unassigned');
        const extra = reviewers.length - reviewerLabels.length;

        return (
          <React.Fragment key={i}>
            <div
              onClick={() => onStageClick?.(i)}
              className={`relative flex flex-col rounded-lg border-2 ${cls} ${focusRing} ${onStageClick ? 'cursor-pointer hover:brightness-110' : ''} ${compact ? 'min-w-[140px] px-2 py-1.5' : 'min-w-[180px] px-3 py-2'}`}
              data-testid={`wf-diagram-stage-${i}`}
            >
              {/* Stage number bubble */}
              <div className="absolute -top-2 -left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-white dark:bg-slate-900 border-2 border-current">
                {isDone ? <CheckCircle2 className="w-3 h-3" />
                       : isRejectedHere ? <XCircle className="w-3 h-3" />
                       : i + 1}
              </div>

              {/* Header row */}
              <div className={`flex items-center justify-between gap-1 ${compact ? 'text-[10px]' : 'text-xs'} font-semibold`}>
                <span className="truncate">{s.name}</span>
                {s.mode === 'parallel' && (
                  <span className="flex items-center text-fuchsia-500" title="Parallel — all reviewers must approve">
                    <Users className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
                  </span>
                )}
                {escalated && <RotateCcw className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-amber-500`} />}
                {overdue && isCurrent && <AlertTriangle className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-red-500`} />}
              </div>

              {/* Reviewers */}
              {!compact ? (
                <div className="mt-1 text-[10px] text-slate-500 truncate">
                  {reviewerLabels.length === 0 ? <span className="italic">no reviewers</span> : reviewerLabels.map((r, idx) => (
                    <span key={idx}>
                      <span className="font-mono">{r}</span>
                      {idx < reviewerLabels.length - 1 && ', '}
                    </span>
                  ))}
                  {extra > 0 && <span> +{extra}</span>}
                </div>
              ) : (
                <div className="mt-0.5 text-[9px] text-slate-500 truncate">
                  {reviewers.length} reviewer{reviewers.length === 1 ? '' : 's'} · {maxSla(s)}d SLA
                </div>
              )}

              {/* Due + SLA + approval count row */}
              {!compact && (
                <div className="mt-1 flex items-center gap-2 text-[9px] text-slate-500">
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> {maxSla(s)}d SLA</span>
                  {dueAt && isCurrent && (
                    <span className={overdue ? 'text-red-500 font-semibold' : ''}>
                      Due {dueAt.toLocaleDateString()}
                    </span>
                  )}
                  {ss.approvals?.length > 0 && (
                    <span className="text-emerald-600 dark:text-emerald-400">✓ {ss.approvals.length}/{s.reviewers.length}</span>
                  )}
                </div>
              )}

              {/* Allowed actions */}
              {!compact && (
                <div className="mt-1 flex flex-wrap gap-0.5">
                  {(s.allowed_actions || []).map(a => (
                    <span key={a} className="text-[8px] px-1 py-0.5 rounded bg-slate-200/60 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400">
                      {a.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {i < stages.length - 1 && (
              <div className={`flex items-center ${isDone ? 'text-emerald-500' : 'text-slate-400'}`}>
                <ArrowRight className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function maxSla(stage) {
  const arr = (stage.reviewers || []).map(r => r.sla_days || 5);
  if (arr.length === 0) return 0;
  return Math.max(...arr);
}
